/**
 * Zentraler, typsicherer Zugriff auf Umgebungsvariablen.
 * Fehlt eine Variable zur Laufzeit, wird ein sprechender Fehler geworfen –
 * statt eines unklaren "undefined" tief im Code.
 */

function optional(name: string, fallback = ""): string {
  return process.env[name] ?? fallback;
}

export const env = {
  // --- E-Mail: bevorzugt eigener SMTP (no-reply@wedev-it.at), sonst Resend ---
  smtpHost: optional("SMTP_HOST"),
  smtpPort: Number(optional("SMTP_PORT", "465")),
  smtpUser: optional("SMTP_USER"),
  smtpPass: optional("SMTP_PASS"),
  // SSL/TLS direkt (Port 465 = true; 587/STARTTLS = false)
  smtpSecure: optional("SMTP_SECURE", "true") !== "false",

  // Resend als Alternative (optional)
  resendApiKey: optional("RESEND_API_KEY"),

  // Absender-Adresse für ausgehende Mails (Login-Codes, Kontaktbestätigung)
  mailFrom: optional("CONTACT_FROM", "WeDev-IT <no-reply@wedev-it.at>"),
  // Zieladresse für Kontaktanfragen
  mailTo: optional("CONTACT_TO", "office@wedev-it.at"),
  /**
   * Google-Apps-Script-Web-App, die Anfragen in ein Sheet schreibt.
   * Leer = abgeschaltet; die Anfrage läuft dann ganz normal weiter.
   */
  sheetWebhookUrl: optional("SHEETS_WEBHOOK_URL", ""),
  /**
   * Gemeinsames Geheimnis mit dem Apps Script. Die Adresse der
   * Web-App ist nicht geheim – ohne dieses Geheimnis schreibt das
   * Skript aber nichts, sonst könnte jeder Zeilen einschleusen.
   */
  sheetWebhookSecret: optional("SHEETS_WEBHOOK_SECRET", ""),

  // Öffentliche Basis-URL
  siteUrl: optional("NEXT_PUBLIC_SITE_URL", "https://wedev-it.at"),

  // Tracking (nur nach Consent aktiv). Leer = deaktiviert.
  gtmId: optional("NEXT_PUBLIC_GTM_ID"),
  gaId: optional("NEXT_PUBLIC_GA_ID"),

  // Auth / Sessions. In Produktion MUSS ein starkes Secret gesetzt sein.
  authSecret: optional("AUTH_SECRET", "dev-insecure-secret-change-me"),
  // Secure-Flag des Session-Cookies. Default: nur in Produktion.
  // Für eine reine HTTP-Vorschau (SSH-Tunnel) via SESSION_COOKIE_SECURE=false abschaltbar.
  sessionCookieSecure:
    process.env.SESSION_COOKIE_SECURE != null
      ? process.env.SESSION_COOKIE_SECURE === "true"
      : process.env.NODE_ENV === "production",

  isProd: process.env.NODE_ENV === "production",

  // --- Marketing-Anbindung (alle optional; ohne Wert bleibt die Funktion aus) ---
  // Google-Cloud-OAuth-Client (Typ „Webanwendung“). Redirect-URI:
  //   <NEXT_PUBLIC_SITE_URL>/api/admin/integrationen/google/callback
  googleClientId: optional("GOOGLE_CLIENT_ID"),
  googleClientSecret: optional("GOOGLE_CLIENT_SECRET"),
  // Google Ads API: Developer-Token aus dem Verwaltungskonto (MCC) und dessen ID.
  googleAdsDeveloperToken: optional("GOOGLE_ADS_DEVELOPER_TOKEN"),
  googleAdsLoginCustomerId: optional("GOOGLE_ADS_LOGIN_CUSTOMER_ID").replace(/\D/g, ""),
  googleAdsApiVersion: optional("GOOGLE_ADS_API_VERSION", "v24"),
  // Die Google-Adresse, die Kunden in Analytics, Tag Manager & Co. freigeben.
  agenturGoogleEmail: optional("AGENTUR_GOOGLE_EMAIL", process.env.CONTACT_TO ?? "office@wedev-it.at"),
  // KI-Auswertung (Claude).
  anthropicApiKey: optional("ANTHROPIC_API_KEY"),
  // Ranking-Abfragen inkl. Mitbewerber (DataForSEO, SERP-API).
  dataforseoLogin: optional("DATAFORSEO_LOGIN"),
  dataforseoPassword: optional("DATAFORSEO_PASSWORD"),
  // PageSpeed Insights (optional, ohne Key nur sehr geringes Kontingent).
  pagespeedApiKey: optional("PAGESPEED_API_KEY"),
  // Regelmäßige Abrufe im Hintergrund. In Produktion standardmäßig an.
  schedulerAktiv:
    process.env.SCHEDULER != null ? process.env.SCHEDULER !== "off" : process.env.NODE_ENV === "production",
} as const;

/** Platzhalter aus .env.example / .env.production.example – niemals produktiv. */
const INSECURE_AUTH_SECRETS = new Set(["dev-insecure-secret-change-me", "HIER_ZUFALLS_SECRET_SETZEN"]);

/**
 * Bricht in Produktion ab, wenn AUTH_SECRET fehlt, ein Platzhalter oder zu
 * kurz ist. Ohne diese Prüfung liefe die App still mit dem öffentlich
 * bekannten Dev-Fallback – Login-Codes (HMAC-Pepper) wären dann für jeden
 * mit Datenbank-Lesezugriff offline knackbar.
 *
 * Wird beim Serverstart aus src/instrumentation-node.ts aufgerufen.
 */
export function assertAuthConfigured() {
  if (!env.isProd) return;
  const secret = process.env.AUTH_SECRET?.trim() ?? "";
  if (secret.length < 32 || INSECURE_AUTH_SECRETS.has(secret)) {
    throw new Error(
      "AUTH_SECRET fehlt, ist ein Platzhalter oder kürzer als 32 Zeichen. " +
        "Bitte ein starkes Secret setzen (openssl rand -base64 32).",
    );
  }
}

/** Für Client-Komponenten: NEXT_PUBLIC_* sind zur Build-Zeit inlined. */
export const publicEnv = {
  gtmId: process.env.NEXT_PUBLIC_GTM_ID ?? "",
  gaId: process.env.NEXT_PUBLIC_GA_ID ?? "",
} as const;

export function assertMailConfigured() {
  if (!env.resendApiKey) {
    throw new Error(
      "RESEND_API_KEY ist nicht gesetzt. Bitte in .env.local hinterlegen (siehe .env.example).",
    );
  }
}
